import React, { useEffect } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Pagination from '../../components/common/Pagination';
import { useFollow } from '../../hooks/useFollow';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../config/theme';
import { UsersIcon } from '../../components/icons/Icons';
import { formatDate, getInitials } from '../../utils/helpers';

const Followers = () => {
  const { user } = useAuth();
  const { following, pagination, loading, fetchFollowing } = useFollow();

  // Reuse fetchFollowing but for seller's followers
  const [followers, setFollowers] = React.useState([]);
  const [followersPagination, setFollowersPagination] = React.useState(null);
  const [localLoading, setLocalLoading] = React.useState(true);

  const loadFollowers = async (page = 1) => {
    setLocalLoading(true);
    try {
      const { followService } = await import('../../services/follow.service');
      const res = await followService.getFollowers(user?.seller?.id || user?.id, { page });
      setFollowers(res.data?.followers || []);
      setFollowersPagination(res.data?.pagination || null);
    } catch {
      setFollowers([]);
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadFollowers();
  }, [user]);

  if (localLoading) return <LoadingSpinner fullScreen />;

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: theme.spacing.xl }}>
          <h1 style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            marginBottom: theme.spacing.xs,
          }}>
            Followers
          </h1>
          <p style={{ color: theme.colors.text.secondary }}>
            {followersPagination?.total ?? 0} customer{followersPagination?.total !== 1 ? 's' : ''} following your store
          </p>
        </div>

        {followers.length === 0 ? (
          <EmptyState
            icon={<UsersIcon size={48} />}
            title="No followers yet"
            description="Customers who follow your store will appear here"
          />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              {followers.map((item) => {
                const u = item.user;
                return (
                  <Card key={item.id} padding="lg">
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                      {/* Avatar */}
                      <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: theme.borderRadius.full,
                        backgroundColor: theme.colors.primary.main,
                        color: theme.colors.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: theme.typography.fontWeight.semibold,
                        fontSize: theme.typography.fontSize.sm,
                        flexShrink: 0,
                      }}>
                        {getInitials(u?.first_name, u?.last_name)}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: theme.typography.fontWeight.semibold,
                          fontSize: theme.typography.fontSize.base,
                          marginBottom: theme.spacing.xs,
                        }}>
                          {u?.first_name} {u?.last_name}
                        </div>
                        <div style={{
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.colors.text.secondary,
                        }}>
                          {u?.email}
                        </div>
                      </div>

                      {/* Follow date */}
                      <div style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.text.disabled,
                        textAlign: 'right',
                        flexShrink: 0,
                      }}>
                        Following since<br />
                        {formatDate(item.created_at)}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div style={{ marginTop: theme.spacing.xl }}>
              <Pagination
                currentPage={followersPagination?.page}
                totalPages={followersPagination?.totalPages}
                hasNextPage={followersPagination?.hasNextPage}
                hasPreviousPage={followersPagination?.hasPreviousPage}
                totalItems={followersPagination?.total}
                itemsPerPage={followersPagination?.limit}
                onPageChange={loadFollowers}
              />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Followers;